(() => {
  const ELEMENT_NAME = 'foope-plus-one-builder';
  const OFFER_NAME = 'FOOPE +1';

  if (customElements.get(ELEMENT_NAME)) return;

  class FoopePlusOneBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.limit = Number(this.dataset.selectionLimit || 4);
      this.cartUrl = this.dataset.cartUrl || (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart';
      this.cartAddUrl = this.dataset.cartAddUrl || (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';
      this.cartType = this.dataset.cartType || (window.theme && window.theme.settings && window.theme.settings.cartType) || 'page';
      this.currencyFormat = this.dataset.currencyFormat || (window.theme && window.theme.defaultMoneyFormat) || '${{amount}}';
      this.selections = [];
      this.activeFilter = 'All';
      this.loading = false;

      const productJSON = this.querySelector('[data-products-json]');
      try {
        this.products = JSON.parse(productJSON ? productJSON.textContent : '[]');
      } catch (error) {
        console.error('FOOPE +1 product data could not be parsed.', error);
        this.products = [];
      }

      this.productsByVariant = new Map(this.products.map((product) => [String(product.variantId), product]));
      this.eligibleVariantIds = new Set(this.products.map((product) => Number(product.variantId)));
      this.dialog = this.querySelector('[data-mobile-dialog]');
      this.mobileBar = this.querySelector('[data-mobile-bar]');
      this.lastFocusedElement = null;

      this.boundClick = this.handleClick.bind(this);
      this.boundDialogCancel = this.handleDialogCancel.bind(this);
      this.addEventListener('click', this.boundClick);
      if (this.dialog) this.dialog.addEventListener('cancel', this.boundDialogCancel);

      this.setupMobileBarObserver();
      this.classList.add('is-ready');
      this.render();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.boundClick);
      if (this.dialog) this.dialog.removeEventListener('cancel', this.boundDialogCancel);
      if (this.mobileBarObserver) this.mobileBarObserver.disconnect();
      delete this.dataset.initialized;
    }

    setupMobileBarObserver() {
      if (!this.mobileBar || !('IntersectionObserver' in window)) {
        if (this.mobileBar) this.mobileBar.classList.add('is-visible');
        return;
      }

      const section = this.closest('.foope-plus-one-section') || this;
      this.mobileBarObserver = new IntersectionObserver((entries) => {
        const visible = entries.some((entry) => entry.isIntersecting);
        this.mobileBar.classList.toggle('is-visible', visible);
      }, { threshold: 0.01 });
      this.mobileBarObserver.observe(section);
    }

    handleDialogCancel(event) {
      event.preventDefault();
      this.closeSummary();
    }

    handleClick(event) {
      const control = event.target.closest('[data-action]');
      if (!control || !this.contains(control)) return;

      const action = control.dataset.action;
      const variantId = control.dataset.variantId;

      switch (action) {
        case 'add':
          this.addSelection(variantId);
          break;
        case 'remove-one':
          this.removeOneSelection(variantId);
          break;
        case 'remove-slot':
          this.removeSlot(Number(control.dataset.slotIndex));
          break;
        case 'clear':
          this.clearSelections();
          break;
        case 'filter':
          this.setFilter(control.dataset.filter || 'All');
          break;
        case 'open-summary':
          this.openSummary(control);
          break;
        case 'close-summary':
          this.closeSummary();
          break;
        case 'add-set':
          this.addSetToCart();
          break;
      }
    }

    selectedQuantityFor(variantId) {
      return this.selections.filter((selection) => String(selection.variantId) === String(variantId)).length;
    }

    addSelection(variantId) {
      if (this.loading || this.selections.length >= this.limit) return;

      const product = this.productsByVariant.get(String(variantId));
      if (!product || !product.available) {
        this.setStatus('That product is currently unavailable.', true);
        return;
      }

      const selectedQuantity = this.selectedQuantityFor(variantId);
      if (product.maxQuantity !== null && selectedQuantity >= Number(product.maxQuantity)) {
        this.setStatus(`Only ${product.maxQuantity} ${product.title} available for this set.`, true);
        return;
      }

      this.selections.push({ ...product });
      this.setStatus('');
      this.announce(`${product.title} added. ${this.selections.length} of ${this.limit} selected.`);
      this.render();
    }

    removeOneSelection(variantId) {
      if (this.loading) return;
      const index = this.selections.map((selection) => String(selection.variantId)).lastIndexOf(String(variantId));
      if (index === -1) return;
      const [removed] = this.selections.splice(index, 1);
      this.setStatus('');
      this.announce(`${removed.title} removed. ${this.selections.length} of ${this.limit} selected.`);
      this.render();
    }

    removeSlot(index) {
      if (this.loading || Number.isNaN(index) || !this.selections[index]) return;
      const [removed] = this.selections.splice(index, 1);
      this.setStatus('');
      this.announce(`${removed.title} removed. ${this.selections.length} of ${this.limit} selected.`);
      this.render();
    }

    clearSelections() {
      if (this.loading || this.selections.length === 0) return;
      this.selections = [];
      this.setStatus('');
      this.announce('FOOPE +1 selections cleared.');
      this.render();
    }

    setFilter(filter) {
      this.activeFilter = filter;
      this.querySelectorAll('[data-action="filter"]').forEach((button) => {
        const active = button.dataset.filter === filter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.querySelectorAll('[data-product-card]').forEach((card) => {
        card.hidden = filter !== 'All' && card.dataset.category !== filter;
      });
    }

    getGiftIndex() {
      if (this.selections.length !== this.limit) return -1;

      let giftIndex = -1;
      let lowestPrice = Number.POSITIVE_INFINITY;

      this.selections.forEach((product, index) => {
        const price = Number(product.price || 0);
        if (price <= lowestPrice) {
          lowestPrice = price;
          giftIndex = index;
        }
      });

      return giftIndex;
    }

    getTotals() {
      const productsTotal = this.selections.reduce((sum, product) => sum + Number(product.price || 0), 0);
      const complete = this.selections.length === this.limit;
      const giftIndex = this.getGiftIndex();
      const giftTotal = giftIndex >= 0 ? Number(this.selections[giftIndex].price || 0) : 0;
      return {
        complete,
        giftIndex,
        productsTotal,
        giftTotal,
        orderTotal: productsTotal - giftTotal,
      };
    }

    formatMoney(cents) {
      if (typeof window.formatMoney === 'function') {
        return window.formatMoney(cents, this.currencyFormat);
      }
      if (typeof formatMoney === 'function') {
        return formatMoney(cents, this.currencyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      }).format(Number(cents || 0) / 100);
    }

    render() {
      const totals = this.getTotals();
      const count = this.selections.length;

      this.querySelectorAll('[data-selected-count]').forEach((element) => { element.textContent = count; });
      this.querySelectorAll('[data-mobile-selected-count]').forEach((element) => { element.textContent = `${count} of ${this.limit}`; });
      this.querySelectorAll('[data-mobile-dialog-count]').forEach((element) => { element.textContent = count; });

      this.querySelectorAll('[data-progress-segment]').forEach((segment, index) => {
        segment.classList.toggle('is-active', index < count);
      });

      this.renderCards(totals);
      this.renderSlots('[data-slots]', totals.giftIndex);
      this.renderSlots('[data-mobile-slots]', totals.giftIndex);
      this.renderTotals(totals);
      this.renderButtons(totals);

      this.querySelectorAll('[data-unlock-message], [data-mobile-unlock-message]').forEach((element) => {
        element.hidden = !totals.complete;
      });

      this.querySelectorAll('[data-action="clear"]').forEach((button) => {
        button.disabled = count === 0 || this.loading;
      });

      const mobileStatus = this.querySelector('[data-mobile-status]');
      if (mobileStatus) mobileStatus.textContent = totals.complete ? 'Your +1 is ready' : 'View your set';

      const mobileTotal = this.querySelector('[data-mobile-total]');
      if (mobileTotal) mobileTotal.textContent = totals.complete ? this.formatMoney(totals.orderTotal) : '—';
    }

    renderCards(totals) {
      this.querySelectorAll('[data-product-card]').forEach((card) => {
        const variantId = card.dataset.variantId;
        const product = this.productsByVariant.get(String(variantId));
        const quantity = this.selectedQuantityFor(variantId);
        const selected = quantity > 0;
        const atLimit = this.selections.length >= this.limit;
        const atInventoryLimit = product && product.maxQuantity !== null && quantity >= Number(product.maxQuantity);

        card.classList.toggle('is-selected', selected);
        const badge = card.querySelector('[data-card-selected-badge]');
        const badgeQuantity = card.querySelector('[data-card-selected-quantity]');
        const addButton = card.querySelector('.foope-plus-one__add-button');
        const quantityControl = card.querySelector('[data-card-quantity-control]');
        const quantityLabel = card.querySelector('[data-card-quantity]');
        const quantityAdd = quantityControl && quantityControl.querySelector('[data-action="add"]');
        const giftBadge = card.querySelector('[data-card-gift-badge]');
        const giftProduct = totals.giftIndex >= 0 ? this.selections[totals.giftIndex] : null;
        const isGiftProduct = Boolean(giftProduct && String(giftProduct.variantId) === String(variantId));

        card.classList.toggle('is-gift', isGiftProduct);
        if (giftBadge) giftBadge.hidden = !isGiftProduct;
        if (badge) badge.hidden = !selected;
        if (badgeQuantity) badgeQuantity.textContent = quantity;
        if (addButton) addButton.hidden = selected;
        if (quantityControl) quantityControl.hidden = !selected;
        if (quantityLabel) quantityLabel.textContent = quantity;
        if (quantityAdd) quantityAdd.disabled = atLimit || atInventoryLimit || !product || !product.available || this.loading;
        if (addButton) addButton.disabled = !product || !product.available || atLimit || atInventoryLimit || this.loading;
      });
    }

    renderSlots(selector, giftIndex) {
      const displaySelections = this.selections.map((product, originalIndex) => ({
        product,
        originalIndex,
        isGift: originalIndex === giftIndex,
      }));

      // Keep the three paid selections together and present the included item as “+1”.
      // The original selection index is retained so removing a visual slot removes the right item.
      if (giftIndex >= 0) {
        const [giftSelection] = displaySelections.splice(giftIndex, 1);
        displaySelections.push(giftSelection);
      }

      this.querySelectorAll(selector).forEach((list) => {
        const slots = list.querySelectorAll(':scope > .foope-plus-one__slot[data-slot-index]');
        slots.forEach((slot, displayIndex) => {
          const entry = displaySelections[displayIndex];
          if (!entry) {
            slot.className = 'foope-plus-one__slot is-empty';
            slot.innerHTML = `
              <span class="foope-plus-one__slot-number">0${displayIndex + 1}</span>
              <span class="foope-plus-one__slot-empty-copy">Pick your ${this.ordinalWord(displayIndex)}</span>
            `;
            return;
          }

          const { product, originalIndex, isGift } = entry;
          slot.className = `foope-plus-one__slot${isGift ? ' is-gift' : ''}`;
          const image = product.image
            ? `<img class="foope-plus-one__slot-image" src="${this.escapeHTML(product.image)}" alt="" loading="lazy">`
            : '<span class="foope-plus-one__slot-image" aria-hidden="true"></span>';
          const meta = isGift
            ? '<span class="foope-plus-one__slot-gift-label">FOOPE +1 · Included</span>'
            : `${this.escapeHTML(product.category)} · ${this.escapeHTML(this.formatMoney(product.price))}`;
          slot.innerHTML = `
            <span class="foope-plus-one__slot-number">${isGift ? '+1' : `0${displayIndex + 1}`}</span>
            ${image}
            <span class="foope-plus-one__slot-copy">
              <span class="foope-plus-one__slot-title">${this.escapeHTML(product.title)}</span>
              <span class="foope-plus-one__slot-meta">${meta}</span>
            </span>
            <button type="button" class="foope-plus-one__slot-remove" data-action="remove-slot" data-slot-index="${originalIndex}" aria-label="Remove ${this.escapeHTML(product.title)} from FOOPE +1">×</button>
          `;
        });
      });
    }

    renderTotals(totals) {
      const productsValue = this.selections.length ? this.formatMoney(totals.productsTotal) : '—';
      const giftValue = totals.complete ? 'Included' : 'Unlock at 4';
      const orderValue = this.selections.length ? this.formatMoney(totals.orderTotal) : '—';

      this.setText('[data-products-total]', productsValue);
      this.setText('[data-gift-total]', giftValue);
      this.setText('[data-order-total]', orderValue);
      this.setText('[data-mobile-products-total]', productsValue);
      this.setText('[data-mobile-gift-total]', giftValue);
      this.setText('[data-mobile-order-total]', orderValue);
    }

    renderButtons(totals) {
      const desktopLabel = totals.complete
        ? `Add my FOOPE +1 — ${this.formatMoney(totals.orderTotal)}`
        : `Pick ${this.limit - this.selections.length} more to unlock your +1`;
      const mobileLabel = totals.complete
        ? `Add my FOOPE +1 — ${this.formatMoney(totals.orderTotal)}`
        : `Pick ${this.limit - this.selections.length} more to unlock your +1`;

      this.setText('[data-submit-label]', desktopLabel);
      this.setText('[data-mobile-submit-label]', mobileLabel);
      this.querySelectorAll('[data-action="add-set"]').forEach((button) => {
        button.disabled = !totals.complete || this.loading;
      });
    }

    setText(selector, value) {
      this.querySelectorAll(selector).forEach((element) => { element.textContent = value; });
    }

    ordinalWord(index) {
      return ['first', 'second', 'third', 'fourth'][index] || `${index + 1}th`;
    }

    openSummary(control) {
      if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
      this.lastFocusedElement = control || document.activeElement;
      if (!this.dialog.open) this.dialog.showModal();
    }

    closeSummary() {
      if (!this.dialog || !this.dialog.open) return;
      this.dialog.close();
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }

    async addSetToCart() {
      const totals = this.getTotals();
      if (!totals.complete || this.loading) return;

      this.setLoading(true);
      this.setStatus('Checking your cart…');
      document.dispatchEvent(new CustomEvent('baseline:cart:lock'));

      try {
        const cart = await this.fetchCart();
        const existingEligibleQuantity = (cart.items || []).reduce((sum, item) => {
          return sum + (this.eligibleVariantIds.has(Number(item.variant_id)) ? Number(item.quantity || 0) : 0);
        }, 0);

        if (existingEligibleQuantity > 0) {
          throw new Error('Remove eligible FOOPE fragrance or candles already in your cart before adding FOOPE +1. This keeps the gift calculation accurate.');
        }

        const groupId = `foope-plus-one-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const groupedSelections = new Map();
        this.selections.forEach((selection) => {
          const key = String(selection.variantId);
          if (!groupedSelections.has(key)) {
            groupedSelections.set(key, { product: selection, quantity: 0 });
          }
          groupedSelections.get(key).quantity += 1;
        });

        const items = Array.from(groupedSelections.values()).map(({ product, quantity }) => ({
          id: Number(product.variantId),
          quantity,
          properties: {
            _foope_plus_one_id: groupId,
            _foope_plus_one_offer: OFFER_NAME,
            _foope_plus_one_source: 'build-your-own-bundle',
          },
        }));

        this.setStatus('Adding your FOOPE +1…');
        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
            sections: 'cart-items,cart-footer,cart-item-count,cart-live-region',
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.status) {
          throw new Error(this.errorMessageFromResponse(data));
        }

        document.body.dispatchEvent(new CustomEvent('baseline:cart:update', {
          bubbles: true,
          detail: { response: data },
        }));

        this.dispatchEvent(new CustomEvent('foope:plus-one:add', {
          bubbles: true,
          detail: {
            groupId,
            items: this.selections.map((selection) => ({
              variantId: selection.variantId,
              productId: selection.productId,
              category: selection.category,
              price: selection.price,
            })),
            productsTotal: totals.productsTotal,
            giftTotal: totals.giftTotal,
            orderTotal: totals.orderTotal,
          },
        }));

        this.selections = [];
        this.setStatus('Added to cart.');
        this.render();
        this.closeSummary();

        if (this.cartType === 'drawer') {
          requestAnimationFrame(() => {
            document.dispatchEvent(new CustomEvent('theme:open:cart-drawer'));
          });
        } else {
          window.location.assign(this.cartUrl);
        }
      } catch (error) {
        console.error('FOOPE +1 add-to-cart error:', error);
        this.setStatus(error.message || 'Your set could not be added. Please try again.', true);
        this.announce(error.message || 'Your set could not be added.');
      } finally {
        document.dispatchEvent(new CustomEvent('baseline:cart:unlock'));
        this.setLoading(false);
      }
    }

    async fetchCart() {
      const cartJSONUrl = this.cartUrl.endsWith('.js') ? this.cartUrl : `${this.cartUrl}.js`;
      const response = await fetch(cartJSONUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Your cart could not be checked. Please refresh and try again.');
      return response.json();
    }

    errorMessageFromResponse(data) {
      if (typeof data.description === 'string') return data.description;
      if (typeof data.message === 'string') return data.message;
      if (typeof data.errors === 'string') return data.errors;
      if (data.errors && typeof data.errors === 'object') {
        return Object.values(data.errors).flat().join(' ');
      }
      return 'Your FOOPE +1 could not be added. Check availability and try again.';
    }

    setLoading(loading) {
      this.loading = loading;
      this.toggleAttribute('aria-busy', loading);
      this.querySelectorAll('button').forEach((button) => {
        if (loading) {
          button.dataset.wasDisabled = button.disabled ? 'true' : 'false';
          button.disabled = true;
        } else if (button.dataset.wasDisabled === 'false') {
          button.disabled = false;
          delete button.dataset.wasDisabled;
        } else {
          delete button.dataset.wasDisabled;
        }
      });
      this.render();
    }

    setStatus(message, isError = false) {
      this.querySelectorAll('[data-status], [data-mobile-status-message]').forEach((element) => {
        element.textContent = message;
        element.classList.toggle('is-error', Boolean(message && isError));
      });
    }

    announce(message) {
      const liveRegion = document.getElementById('screenreader-announce');
      if (!liveRegion) return;
      liveRegion.textContent = '';
      window.setTimeout(() => { liveRegion.textContent = message; }, 20);
    }

    escapeHTML(value) {
      return String(value == null ? '' : value)
        .replaceAll('&', '&amp;')
        .replaceAll('<', '&lt;')
        .replaceAll('>', '&gt;')
        .replaceAll('"', '&quot;')
        .replaceAll("'", '&#039;');
    }
  }

  customElements.define(ELEMENT_NAME, FoopePlusOneBuilder);
})();
